import type { MvActivityType } from "@/db/schema/movement";
import { buildRoutePreview } from "./route-preview";

export type MovementPostType = "run" | "walk" | "ride" | "hike";

export type MovementActivityPostPayload = {
  sourceType: "movement_activity";
  movementActivityId: string;
  movementType: MovementPostType;
  title?: string;
  description?: string;
  distanceMeters: number;
  durationSeconds: number;
  avgPaceSecondsPerKm?: number;
  elevationGainMeters?: number;
  elevationLossMeters?: number;
  previewPoints: [number, number][];
  previewSegments?: [number, number][][];
  shareVariant?: string;
  moduleActivationId?: number;
};

export function toMovementPostType(
  activityType: MvActivityType | string | null | undefined,
): MovementPostType {
  switch (activityType) {
    case "walk":
      return "walk";
    case "ride":
      return "ride";
    case "hike":
      return "hike";
    default:
      return "run";
  }
}

export function buildMovementActivityPostPayload(input: {
  movementActivityId: string;
  movementType: MovementPostType;
  title?: string | null;
  description?: string | null;
  distanceMeters?: number | null;
  durationSeconds?: number | null;
  avgPaceSecondsPerKm?: number | null;
  elevationGainMeters?: number | null;
  elevationLossMeters?: number | null;
  points?: [number, number][];
  routePoints?: {
    latitude: number;
    longitude: number;
    segmentId?: number | null;
  }[];
  previewPointsLimit?: number;
  shareVariant?: string | null;
  moduleActivationId?: number | null;
}): MovementActivityPostPayload {
  const preview = buildRoutePreview(
    input.routePoints ??
      (input.points ?? []).map(([latitude, longitude]) => ({
        latitude,
        longitude,
      })),
    Math.max(20, Math.min(input.previewPointsLimit ?? 240, 360)),
  );

  const payload: MovementActivityPostPayload = {
    sourceType: "movement_activity",
    movementActivityId: input.movementActivityId,
    movementType: input.movementType,
    distanceMeters: Math.max(0, input.distanceMeters ?? 0),
    durationSeconds: Math.max(0, Math.floor(input.durationSeconds ?? 0)),
    ...preview,
  };

  const title = input.title?.trim();
  if (title) payload.title = title;

  const description = input.description?.trim();
  if (description) payload.description = description;

  if (
    typeof input.avgPaceSecondsPerKm === "number" &&
    Number.isFinite(input.avgPaceSecondsPerKm) &&
    input.avgPaceSecondsPerKm > 0
  ) {
    payload.avgPaceSecondsPerKm = input.avgPaceSecondsPerKm;
  }

  if (
    typeof input.elevationGainMeters === "number" &&
    Number.isFinite(input.elevationGainMeters) &&
    input.elevationGainMeters > 0
  ) {
    payload.elevationGainMeters = input.elevationGainMeters;
  }

  if (
    typeof input.elevationLossMeters === "number" &&
    Number.isFinite(input.elevationLossMeters) &&
    input.elevationLossMeters > 0
  ) {
    payload.elevationLossMeters = input.elevationLossMeters;
  }

  if (
    typeof input.moduleActivationId === "number" &&
    Number.isInteger(input.moduleActivationId) &&
    input.moduleActivationId > 0
  ) {
    payload.moduleActivationId = input.moduleActivationId;
  }

  if (input.shareVariant?.trim()) {
    payload.shareVariant = input.shareVariant.trim();
  }

  return payload;
}

function isValidCoordinatePair(value: unknown): value is [number, number] {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === "number" &&
    Number.isFinite(value[0]) &&
    Math.abs(value[0]) <= 90 &&
    typeof value[1] === "number" &&
    Number.isFinite(value[1]) &&
    Math.abs(value[1]) <= 180
  );
}

export function isMovementActivityPostPayload(
  value: unknown,
): value is MovementActivityPostPayload {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const record = value as Record<string, unknown>;
  if (record.sourceType !== "movement_activity") return false;
  if (typeof record.movementActivityId !== "string") return false;
  if (
    record.movementType !== "run" &&
    record.movementType !== "walk" &&
    record.movementType !== "ride" &&
    record.movementType !== "hike"
  ) {
    return false;
  }

  if (
    typeof record.distanceMeters !== "number" ||
    !Number.isFinite(record.distanceMeters)
  ) {
    return false;
  }

  if (
    typeof record.durationSeconds !== "number" ||
    !Number.isFinite(record.durationSeconds)
  ) {
    return false;
  }

  if (!Array.isArray(record.previewPoints)) return false;
  if (!record.previewPoints.every(isValidCoordinatePair)) return false;
  return (
    record.previewSegments === undefined ||
    (Array.isArray(record.previewSegments) &&
      record.previewSegments.every(
        (segment) =>
          Array.isArray(segment) && segment.every(isValidCoordinatePair),
      ))
  );
}
